import _flattenDeep from 'lodash/flattenDeep';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _sortBy from 'lodash/sortBy';
import _some from 'lodash/some';
import {
  getAlgoliaListFilters,
  getKeyFromTitle,
  sortByScoringWithoutRepeatedTypes,
} from 'containers/EditorList/utils';
import algolia from 'utils/algoliaService';
import Config from 'utils/getEnvConfig';
import { getTopicListResourcesAttributesToRetrieve } from '../utils';

const {
  ALGOLIA: { RESOURCES_AVG_RATED_INDEX, RESOURCES_PUBLISHED_AT_INDEX },
} = Config;

const getTopicListResources = async (_key, params) => {
  const {
    slimItems,
    profileFilters = {},
    clientDetails,
    client,
    clientExcludedTopic,
    clientExcludedResourceTypes,
    clientExcludedCost,
    clientListResourcesWeights,
    isPeerReview = false,
    averageRatingCutoff,
    expertRatingCutoff,
    userRatingCutoff,
    currentFilters,
    topicCenterFiltersSiteCopy,
    excludedAssessments,
    algoliaLocale,
    averageRatingLimit,
    shouldFilterShortArticles,
    featuresConfig,
    attributesToHighlight = [],
    attributesToRetrieve,
    filtersTranslationsSiteCopy,
  } = params;

  const {
    audiences: profileAudiences = [],
    tags: profileTags = [],
    excludedAudiences = [],
    excludedTags = [],
  } = profileFilters;

  const query = _get(currentFilters, 'query', '');
  const audiencesFilters = _get(currentFilters, 'audience', []);
  const clientFilters = _get(currentFilters, 'client', []);
  const costFilters = _get(currentFilters, 'cost', []);
  const languageFilters = _get(currentFilters, 'language', []);
  const lengthFilters = _get(currentFilters, 'length', []);
  const tagsFilters = _get(currentFilters, 'tags', []);
  const currentTypesFilters = _get(currentFilters, 'types', []);
  const collectionFilters = _get(currentFilters, 'collection', []);
  const collectionTypesFilters = [];

  const prefix = algoliaLocale !== 'en-US' ? `${algoliaLocale}_` : '';
  const attrsToRetrieve =
    attributesToRetrieve || getTopicListResourcesAttributesToRetrieve(prefix);

  if (!_isEmpty(collectionFilters)) {
    const personalizeB = _get(
      topicCenterFiltersSiteCopy,
      ['pageCopy', 'personalize-b'],
      {},
    );
    const collections = _flattenDeep(
      Object.keys(personalizeB)
        .filter(key => personalizeB[key].behavior === 'collection')
        .map(key => personalizeB[key].values),
    );
    collectionFilters.forEach(item => {
      const [{ values }] = collections.filter(el => el.label === item);
      collectionTypesFilters.push(...values);
    });
  }
  const typesFilters = currentTypesFilters.concat(collectionTypesFilters);

  const hasActiveFilter =
    !_isEmpty(_get(currentFilters, 'audience')) ||
    !_isEmpty(_get(currentFilters, 'collection', [])) ||
    !_isEmpty(_get(currentFilters, 'types', [])) ||
    !_isEmpty(_get(currentFilters, 'tags', [])) ||
    !_isEmpty(_get(currentFilters, 'language', [])) ||
    !_isEmpty(_get(currentFilters, 'cost', [])) ||
    !_isEmpty(_get(currentFilters, 'length', [])) ||
    !_isEmpty(_get(currentFilters, 'client', []));

  const clientOptionalFilters = [];
  if (!_isEmpty(clientListResourcesWeights)) {
    clientOptionalFilters.push(
      ...Object.keys(clientListResourcesWeights).map(field =>
        Object.keys(clientListResourcesWeights[field]).map(
          filter =>
            `${field}:${filter}<score=${clientListResourcesWeights[field][filter]}>`,
        ),
      ),
    );
  }

  let shouldDecreaseRepeatedTypeScoring = [];
  const resourcesQueries = slimItems.map(item => {
    if (!item.isAutomatic) {
      shouldDecreaseRepeatedTypeScoring.push(null);
      return null;
    }

    const context = _get(item, 'context', '');
    const searchTerm = _get(context, 'searchTerm', '');

    if (searchTerm && query) {
      shouldDecreaseRepeatedTypeScoring.push(null);
      return null;
    }

    const types = [];
    if (!_isEmpty(typesFilters)) {
      const listTypes = _get(item, 'types');
      typesFilters.forEach(type => {
        if (_isEmpty(listTypes) || listTypes.includes(type)) types.push(type);
      });

      if (_isEmpty(types)) types.push(...listTypes);
    } else {
      const listTypes = _get(item, 'types');
      if (listTypes) types.push(...listTypes);
    }

    const audiences = _get(item, 'audiences');
    const tags = _get(item, 'tags', []);

    const {
      currentTopic,
      topics,
      themesTopics,
      handpicked,
      hasOnlyNewsType,
    } = item;

    let definitiveIndex;
    if (hasOnlyNewsType) {
      definitiveIndex = RESOURCES_PUBLISHED_AT_INDEX;
    } else {
      definitiveIndex = RESOURCES_AVG_RATED_INDEX;
      shouldDecreaseRepeatedTypeScoring.push(!handpicked.length);
    }

    const algoliaParams = {
      context,
      types,
      currentTopic,
      isPeerReview,
      topics,
      themesTopics,
      audiences,
      tags,
      handpicked,
      cost: costFilters,
      length: lengthFilters,
      languages: languageFilters,
      profileFilters,
      clientDetails,
      client,
      clientExcludedTopic,
      clientExcludedResourceTypes,
      clientExcludedCost,
      averageRatingCutoff,
      expertRatingCutoff,
      userRatingCutoff,
      excludedAssessments,
      excludedAudiences: hasActiveFilter ? [] : excludedAudiences,
      excludedTags: hasActiveFilter ? [] : excludedTags,
      locale: algoliaLocale,
      shouldApplyLengthFilter: item.shouldApplyLengthFilter,
      averageRatingLimit,
      shouldFilterShortArticles,
      featuresConfig,
      filtersTranslationsSiteCopy,
    };

    const optionalFilters = [];
    if (
      !_isEmpty(typesFilters) &&
      !_isEqual(_sortBy(typesFilters), _sortBy(types))
    ) {
      optionalFilters.push(
        typesFilters.map(filter => `${prefix}expandedType:${filter}`),
      );
    }
    if (
      !_isEmpty(audiencesFilters) &&
      !_isEqual(_sortBy(audiencesFilters), _sortBy(audiences))
    ) {
      optionalFilters.push(
        audiencesFilters.map(
          filter =>
            `expandedAudience:${getKeyFromTitle(
              filtersTranslationsSiteCopy,
              'audience',
              filter,
            )}`,
        ),
      );
    } else if (!_isEmpty(profileAudiences)) {
      optionalFilters.push(
        profileAudiences.map(filter => `expandedAudience:${filter.id}`),
      );
    }
    if (
      !_isEmpty(tagsFilters) &&
      !_isEqual(_sortBy(tagsFilters), _sortBy(tags))
    ) {
      optionalFilters.push(
        tagsFilters.map(
          filter =>
            `expandedTags:${getKeyFromTitle(
              filtersTranslationsSiteCopy,
              'tags',
              filter,
            )}`,
        ),
      );
    } else if (!_isEmpty(profileTags)) {
      optionalFilters.push(
        profileTags.map(filter => `expandedTags:${filter.id}`),
      );
    }
    if (!_isEmpty(lengthFilters)) {
      optionalFilters.push(['underFiveMinutes:true']);
    }
    if (!_isEmpty(clientFilters) || params.prioritizeClientResources) {
      optionalFilters.push([
        `client.shortName:${clientDetails.shortName}<score=2>`,
      ]);
      if (!_isEmpty(_get(clientDetails, 'clientGroup'))) {
        optionalFilters.push([
          `client.shortName:${_get(
            clientDetails,
            'clientGroup.shortName',
          )}<score=1>`,
        ]);
      }
    }

    if (!_isEmpty(clientOptionalFilters)) {
      optionalFilters.push(...clientOptionalFilters);
    }

    return {
      indexName: definitiveIndex,
      query: query || searchTerm,
      params: {
        typoTolerance: 'min',
        ignorePlurals: true,
        queryLanguages: ['en'],
        page: 0,
        hitsPerPage: 20,
        getRankingInfo: false,
        filters: getAlgoliaListFilters(algoliaParams),
        optionalFilters,
        advancedSyntax: true,
        attributesToRetrieve: attrsToRetrieve,
        attributesToHighlight,
      },
    };
  });
  shouldDecreaseRepeatedTypeScoring = shouldDecreaseRepeatedTypeScoring.filter(
    item => item !== null,
  );
  if (resourcesQueries.filter(item => item !== null).length > 0) {
    const requests = Array.from(
      new Array(
        Math.ceil(resourcesQueries.filter(item => item !== null).length / 50),
      ),
    ).map((_, k) =>
      algolia.multipleQueries(
        resourcesQueries
          .filter(item => item !== null)
          .slice(k * 50, (k + 1) * 50),
      ),
    );
    const response = await Promise.all(requests);
    const resourcesResponse = response.reduce(
      (acc, value) => acc.concat(value.results),
      [],
    );
    const finalResponses = resourcesResponse.map((singleResponse, i) => {
      const peopleOrganizationsSlugs = [];

      const newHits = [];
      const listTail = [];
      (shouldDecreaseRepeatedTypeScoring[i]
        ? sortByScoringWithoutRepeatedTypes(
            singleResponse.hits,
            params.prioritizeClientResources,
          )
        : singleResponse.hits
      ).forEach(hit => {
        if (newHits.length < 8) {
          if (['Podcasts', 'Videos', 'Articles'].includes(hit.type)) {
            const peopleSlugs = Array.isArray(hit.person)
              ? hit.person.map(el => _get(el, 'fields.slug'))
              : _get(hit, 'person.fields.slug')
              ? [hit.person.fields.slug]
              : [];
            const organizationsSlugs = Array.isArray(hit.organization)
              ? hit.organization.map(el => _get(el, 'fields.slug'))
              : _get(hit, 'organization.fields.slug')
              ? [hit.organization.fields.slug]
              : [];
            const compositionSlugs = peopleSlugs.concat(organizationsSlugs);

            if (
              _some(compositionSlugs, el =>
                peopleOrganizationsSlugs.includes(el),
              )
            ) {
              listTail.push(hit);
            } else {
              newHits.push(hit);
              peopleOrganizationsSlugs.push(...compositionSlugs);
            }
          } else {
            newHits.push(hit);
          }
        }
      });

      return {
        ...singleResponse,
        hits: newHits.concat(listTail).slice(0, 10),
      };
    });

    return resourcesQueries.map(item =>
      item === null ? null : finalResponses.shift(),
    );
  }
  return resourcesQueries;
};

export default getTopicListResources;
